/**
 * Centralized color mapping for expenditure types
 *
 * Palette source: DZ Infografiken Figma (vrfxzr9veVQOEWyOVqydZZ) — the same
 * corporate-design catalog the dz-publishing-process chart engine renders
 * with. The first 7 entries are the engine's dataSeries order; the rest are
 * the Figma color-catalog tones (Violett/Pink, Rot/Braun, Blau/Türkis,
 * Grün/Olive, Gelb/Braun) interleaved across groups so neighbouring series
 * stay distinguishable.
 */
export const COLORS = [
  "#DC758A", // Pink
  "#81A1E3", // Light Blue
  "#DABDAE", // Rose-Beige
  "#6E731A", // Olive
  "#81548D", // Violet
  "#478469", // Green
  "#FF794D", // Orange
  "#325EA9", // Blue
  "#D7462A", // Red
  "#E7DE7A", // Yellow
  "#185C3D", // Dark Green
  "#80A5BC", // Blue-Gray
  "#C4A72A", // Yellow-Orange
  "#5D3B65", // Dark Violet
  "#F49674", // Light Red
  "#2F3F60", // Dark Navy
  "#DAAA73", // Orange-Beige
  "#409D74", // Mid Green
  "#74430A", // Dark Brown
  "#8ECCB0", // Light Green
  "#4F6490", // Dark Blue
  "#9E9931", // Olive Yellow
  "#D5C793", // Beige
  "#2B4C31", // Forest Green
] as const;

// Static mapping of expenditure_type to color.
// Where the Figma "Haushaltstitel" example assigns a category explicitly we
// use exactly that color; the remaining categories get catalog tones (same
// color family = gradation for related categories, per the Figma rules).
export const EXPENDITURE_TYPE_COLORS: Record<string, string> = {
  "Verwaltungskosten": "#E7DE7A",               // Yellow
  "Öffentliche Leistungen": "#D7462A",          // Figma
  // The two Subventionen bands are the base of the stack and read as one
  // block: same family, two tones (catalog Blue / Light Blue), darker at the
  // bottom. They keep their distance from the navy Verwaltungskosten, which
  // sits further up with the red Öffentliche Leistungen and olive
  // Verteidigung between them.
  "Subventionen an Haushalte": "#325EA9",       // Blue
  "Subventionen an Unternehmen": "#81A1E3",     // Light Blue — gradation of the above
  "Finanzierungskosten": "#8ECCB0",             // Figma
  "Verteidigung": "#6E731A",                    // Figma
  // Haushaltstechnik renders as a dashed black outline with no fill (Figma);
  // black here drives the dashed legend dot / stroke color.
  "Haushaltstechnik & Sonderposten": "#000000",
  "Internationales": "#185C3D",                 // Figma
  "Kosten historischer Ereignisse & Altlasten": "#81548D",
  "Sonstige Posten": "#848484",
  "Verwaltung": "#4F6490",                      // Dark Blue
  // Handle typo in data
  "Suventionen an Unternehmen": "#81A1E3",
};

// Static mapping of einnahmen_type (revenue) to color (no explicit Figma
// design exists for Einnahmen — assigned from the same catalog)
export const EINNAHMEN_TYPE_COLORS: Record<string, string> = {
  "Umsatzsteuer": "#325EA9",            // Blue (largest revenue source)
  "Einkommen- & Lohnsteuer": "#409D74", // Mid Green
  "Körperschaftsteuer": "#185C3D",      // Dark Green
  "Energiesteuern": "#FF794D",          // Orange
  "Kapitalertragsteuer": "#81548D",     // Violet
  "Sonstige Steuern": "#DAAA73",        // Orange-Beige
  "Sonstige Einnahmen": "#848484",      // Gray (for "misc")
};

// Fallback color for unknown types (Figma catalog gray)
export const FALLBACK_COLOR = "#848484";

// Special color for "Sonstiges" (Others) bucket
export const SONSTIGES_COLOR = "#848484";
export const SONSTIGES_ID = "SONSTIGES";

/**
 * Get the color for an expenditure or einnahmen type
 */
export function getExpenditureTypeColor(type: string | null | undefined): string {
  if (!type) return FALLBACK_COLOR;
  // Check Ausgaben colors first, then Einnahmen colors
  return EXPENDITURE_TYPE_COLORS[type] ?? EINNAHMEN_TYPE_COLORS[type] ?? FALLBACK_COLOR;
}

/**
 * Get color for sub-expenditure types (by index)
 */
export function getSubTypeColor(index: number): string {
  return COLORS[index % COLORS.length];
}
